import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { stringDateTimeWithLocaleFromApiStringInTz, formatDateFromStringWithLocale } from '@utils/dateWithTz';
import { Button } from 'primereact/button';
import UploadAttachmentDialog, { AttachmentDocument } from './UploadAttachmentDialog';
import { Checkbox } from 'primereact/checkbox';
import { formatNumber, isNotNullOrUndefined } from '@utils/utils';
import ShareDocumentDialog from './ShareDocumentDialog/ShareDocumentDialog';
import CopyDocumentsDialog from './CopyDocumentsDialog/CopyDocumentsDialog';
import {
  AccessTypeEnum,
  AttachmentUploadedOutDTO,
  AttachmentWebhookStateEnum,
  BigDecimalWithErrorsDTO,
  DocumentTypeEnum,
  EntityTypeEnum,
  FeaturesEnum,
  InvoiceErrorDTO,
  InvoicePriceDTO,
  InvoiceSeverityEnum,
  LabelPreviewWithoutTypeOutDTO,
  PermissionTypesEnum,
  StringWithErrorsDTO,
} from '@api/ringil-api.v2';
import useMassAction from '@hooks/useMassAction/useMassAction';
import { Clickable, HorizontalLine, Note, RingilH2 } from '@components/styles';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import PDFViewerDialog from '@components/obsolete/PDFViewer/PDFViewerDialog.tsx';
import LinkSvg from '@icons/LinkSvg';
import EyeSvg from '@icons/EyeSvg';
import { AttachmentLabelsBar } from './AttachmentLabelsBar';
import { ActionPanel } from '@components/ringil3/Features/Attachments/ActionPanel';
import SvgMoreButton from '@components/ringil3/icons/buttons/MoreButton';
import EditAttachmentSidebar from './EditAttachmentSidebar';
import EditInvoiceSidebar from './EditInvoiceSidebar';
import { AiFileButton } from '@components/ai/AiFileButton.tsx';
import SendTestInvoiceDataSidebar from '@components/ringil3/Features/Attachments/SendTestInvoiceDataSidebar.tsx';
import clsx from 'clsx';
import {
  getBorderClassForInvoiceState,
  getInvoiceActionInfo,
  getInvoiceMessageArgs
} from '@components/ai/invoiceStateUtils.ts';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import SvgClose from '@icons/close.svg?react';
import SvgLink from '@icons/link.svg?react';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgERROR from '@icons/alert-circle.svg?react';
import SvgWARNING from '@icons/alert.svg?react';
import SvgRepeat from '@icons/repeat.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { useMAAttachment } from '@components/ringil3/Features/Attachments/MAAttachment.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { downloadBase64File, downloadBlob } from '@utils/download';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { usePushEvent } from '@hooks/usePush/usePushEvent.ts';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import dayjs from 'dayjs';

const MAX_ZIP_FILES = 100;
const MAX_ZIP_SIZE_BYTES = 500 * 1024 * 1024;

interface LabelDefinition {
  labelId: string;
  labelValue: string;
  labelColor: string;
}

interface AttachmentComponentOptions {
  enableCopyDocument: boolean;
  initialAttachments?: AttachmentUploadedOutDTO[];
  doNotShare?: boolean;
  disabled?: boolean;
  // Permissions gating the "Edit invoice" row action; defaults to the expedition one (entity-specific callers pass their own, e.g. SN).
  invoicesRwPermissions?: PermissionTypesEnum[];
  enableDownloadZip?: boolean;
  downloadZipApplicationId?: string;
  // Called after the attachments were changed here (added, edited, deleted, copied) — e.g. to refresh a list showing them.
  onChanged?: () => void;
}

interface ComponentProps {
  onAddAttachment?: (attachments: AttachmentDocument[]) => void;
  onDeletedAttachment?: (attachmentId: string) => void;
  onDeleteMultipleAttachments?: (attachmentIdsToDelete: string[]) => void;
  entityType: EntityTypeEnum;
  entityId: string | null;
  onChangeVisibility?: (attachmentId: string, internal: boolean) => void;
  disabled?: boolean;
  secured?: SecuredConfig;
  disabledDelete?: boolean;
  disabledMassAction?: boolean;
  // Separately gates the "Add attachment" button; defaults to `disabled` when not provided.
  disabledUpload?: boolean;
  allowAddNewAttachment?: boolean;
  allowEditAttachment?: boolean;
  // Opt-in: hide the component's own "Documents" heading so the caller can supply its own section header.
  hideHeader?: boolean;
  loadAttachmentsForEntity?: (
    entityType: string,
    entityId: string,
    onSuccess: (data: AttachmentUploadedOutDTO[]) => void,
    hashAuthentication?: string,
    callbacks?: any,
  ) => void;
  securityCode?: string | null;
  onAttachmentEdited?: (update: {
    attachmentId: string;
    changedDtoFields: Partial<AttachmentUploadedOutDTO>;
    newLabelIds: string[];
    newLabels: LabelPreviewWithoutTypeOutDTO[];
  }) => void;
}

const AttachmentTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: 'DM Sans', sans-serif;
  margin-top: 3px;

  .p-datatable .p-datatable-thead > tr > th {
    background-color: #e7ebee !important;
    border-color: #e7ebee !important;
    color: #343333 !important;
    padding: 1rem;
    font-size: 1rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
  }
  .p-datatable .p-datatable-tbody > tr > td {
    box-sizing: border-box;
    padding: 1rem;
    vertical-align: top;
    font-size: 1rem;
    font-family: 'DM Sans', sans-serif;
    background: #fff;
    border-bottom: 1px solid #e7ebee;
  }
  .p-datatable .p-datatable-tbody > tr {
    color: #616061;
    transition: background 0.2s;
    &:hover {
      background: #f7f8f9;
    }
  }
  .p-datatable .p-datatable-wrapper {
    margin: 0;
    padding: 0;
  }
  .p-datatable {
    padding: 0;
    background: #f7f8f9;
    border-radius: 8px;
    box-shadow: none;
  }
  .p-checkbox-box {
    border-radius: 4px !important;
    border: 1.5px solid #e7ebee !important;
    width: 20px !important;
    height: 20px !important;
    background: #fff !important;
    transition: border-color 0.2s;
    &:hover {
      border-color: #ff6b00 !important;
    }
  }
  .p-checkbox-box.p-highlight {
    border-color: #ff6b00 !important;
    background: #ff6b00 !important;
  }
  .p-checkbox-icon {
    color: #fff !important;
    font-size: 1rem !important;
  }
  .p-button {
    border-radius: 4px !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 1rem !important;
    background: #fff !important;
    color: #fe5000 !important;
    border: 1px solid #e7ebee !important;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
    &:hover {
      background: #fff5f0 !important;
      color: #ff6b00 !important;
      border-color: #ff6b00 !important;
    }
  }
  .p-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .badge-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }
  .p-datatable .p-datatable-thead > tr > th,
  .p-datatable .p-datatable-tbody > tr > td {
    @media (max-width: 640px) {
      padding: 0.5rem;
      font-size: 0.95rem;
    }
  }
  .p-datatable {
    @media (max-width: 640px) {
      font-size: 0.95rem;
    }
  }
`;

// @ts-ignore
const StyledRingilH2 = styled(RingilH2)`
  font-size: 1.4rem;
  font-weight: 400;
  letter-spacing: -0.05em;
  margin: 1rem 0;
`;

const IconButton = styled.button`
  border: 1px solid #b2b2b2;
  box-shadow: rgba(254, 80, 0, 0.15) 0px 4px 4px 0px;

  &:hover {
    border-color: #ff6b00;
    box-shadow: 0px 4px 8px 0px rgba(254, 80, 0, 0.18);
  }
`;

const DocumentTypeTag = styled.span`
  display: inline-block;
  background: #616061;
  color: #ffffff;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.95em;
  font-weight: 500;
  width: fit-content;
`;

const PublicTag = styled.span`
  display: inline-block;
  background: #99e8bf;
  color: #343333;
  border-radius: 4px;
  padding: 4px 8px;
  width: fit-content;
`;

const InternalTag = styled.span`
  display: inline-block;
  background: #fff4a6;
  color: #343333;
  border-radius: 4px;
  padding: 4px 8px;
  width: fit-content;
`;




const Details = styled.details`
  position: relative;
`;

const Summary = styled.summary`
  list-style: none;
  cursor: pointer;
  &::-webkit-details-marker {
    display: none;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #b2b2b2;
  box-shadow: rgba(254, 80, 0, 0.15) 0px 4px 4px 0px;
  padding: 0;
  &:hover {
    border-color: #ff6b00;
    box-shadow: 0px 4px 8px 0px rgba(254, 80, 0, 0.18);
  }
  &:active {
    box-shadow: none;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.4rem;
  background: white;
  border: 1px solid #e7ebee;
  z-index: 1001;
  padding: 10px 0;
  min-width: 140px;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px 0 rgba(31, 41, 55, 0.08);
`;

/**
 * Everything the panel needs, owned by the caller so it survives that page re-rendering.
 *
 * Split out from the component on purpose: the component itself lives at module scope so its type is
 * stable, and receives this as one `internals` prop. See useSidebarControl for the full story.
 */
const useAttachmentInternals = (
  {
    enableCopyDocument,
    initialAttachments = null,
    doNotShare = false,
    disabled = false,
    invoicesRwPermissions,
    enableDownloadZip = false,
    downloadZipApplicationId,
    onChanged,
  }: AttachmentComponentOptions,
  componentProps: ComponentProps,
) => {
  const { listDocumentsForEntity, downloadAttachment, deleteDocumentForEntity, genericReProcess, downloadAttachmentsZip } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const [attachments, setAttachments] = useState<AttachmentUploadedOutDTO[]>(initialAttachments);
  const [expandedRows, setExpandedRows] = useState<AttachmentUploadedOutDTO[]>([]);
  const [visibleShareDialog, setVisibleShareDialog] = useState(false);
  const [visibleUploadDialog, setVisibleUploadDialog] = useState(false);
  const [visibleCopyDocuments, setVisibleCopyDocuments] = useState(false);
  const [visibleEditSidebar, setVisibleEditSidebar] = useState(false);
  const [visibleEditInvoiceSidebar, setVisibleEditInvoiceSidebar] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<AttachmentUploadedOutDTO | null>(null);
  const [visibleSendTestSidebar, setVisibleTestSidebar] = useState(false);
  const [downloadZipBusy, setDownloadZipBusy] = useState(false);

  const { tr } = useTranslationLgs();

  const { MassActionAttachment, setVisible: setMassActionVisible } = useMAAttachment();

  const testRunsFlag = useSecuredFlag('attachments-run-test', {
    kind: 'action',
    description: 'Send test invoice data menu item',
    permissions: [PermissionTypesEnum.PERMISSION_RINGIL_ALLOW_RUN_TEST],
  });
  const canEditInvoicesFlag = useSecuredFlag('attachments-edit-invoice', {
    kind: 'visibility',
    description: 'Edit invoice menu item',
    permissions: invoicesRwPermissions ?? [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_INVOICES_RW],
  });

  const { entityType, entityId, loadAttachmentsForEntity, securityCode } = componentProps;

  useEffect(() => {
    if (isNotNullOrUndefined(initialAttachments)) {
      setAttachments(initialAttachments);
    } else {
      setAttachments([]); // Ensure it's an empty array if initialAttachments is not provided
    }
  }, [initialAttachments]);

  const applyLoadedAttachments = (apiResult: AttachmentUploadedOutDTO[]) => {
    setAttachments(apiResult);
    setSelectedObjects(prev => prev.filter(s => apiResult.some(a => a.attachmentId === s.attachmentId)));
  };

  const load = (currentEntityType: EntityTypeEnum, currentEntityId: string | null) => {
    if (currentEntityId === null) {
      // If initialAttachments is the source of truth, ensure state reflects it.
      // The useEffect above handles syncing initialAttachments to the local 'attachments' state.
      // If initialAttachments itself is not up-to-date, that's an issue in the parent.
      // We can force a sync here if really needed, but it might be redundant with the useEffect.
      if (isNotNullOrUndefined(initialAttachments)) setAttachments(initialAttachments);
      return;
    }
    if (loadAttachmentsForEntity) {
      loadAttachmentsForEntity(currentEntityType, currentEntityId, applyLoadedAttachments, securityCode);
    } else {
      listDocumentsForEntity(currentEntityType, currentEntityId, applyLoadedAttachments, securityCode);
    }
  };

  // After a change made here: the fresh list, and the page told.
  const reloadAfterChange = () => {
    load(entityType, entityId);
    onChanged?.();
  };

  // RD-3076: the upload webhook (Notino eTransport RO) finished - reload so the row shows the UIT or the
  // failure instead of "sending". The push is only a hint; the list endpoint returns the state.
  usePushEvent(
    message => {
      const data = message.data ?? message;
      const concernsThisList =
        (entityId !== null && data.entityId === entityId) || attachments?.some(a => a.attachmentId === data.attachmentId);
      if (concernsThisList) reloadAfterChange();
    },
    {
      types: ['attachment.webhook'],
      onReconnect: () => {
        if (attachments?.some(a => a.attachmentWebhookState === AttachmentWebhookStateEnum.PENDING)) reloadAfterChange();
      },
    },
  );

  // Effect to reload if entityId changes from null to a value, or if initialAttachments change when entityId is null
  useEffect(() => {
    if (entityId !== null) {
      load(entityType, entityId);
    } else {
      // If entityId is null, ensure initialAttachments are reflected
      // This is also covered by the initialAttachments useEffect, but good for clarity
      load(entityType, null);
    }
  }, [entityType, entityId, initialAttachments, loadAttachmentsForEntity]);

  const downloadFile = (attachmentId: string) => {
    downloadAttachment(
      attachmentId,
      data => {
        downloadBase64File(data.data, data.mimeType, data.fileName);
      },
      securityCode,
    );
  };

  const downloadZip = (selected: AttachmentUploadedOutDTO[]) => {
    if (selected.length === 0 || downloadZipBusy) return;
    if (selected.length === 1) {
      downloadFile(selected[0].attachmentId);
      return;
    }
    if (selected.length > MAX_ZIP_FILES) {
      showToastMessage(
        tr('AttachementComponent.downloadZipTooManyFiles', 'At most {{max}} files can be downloaded at once', { max: MAX_ZIP_FILES }),
        null,
        'warn',
      );
      return;
    }
    const totalSize = selected.reduce((sum, a) => sum + (a.size ?? 0), 0);
    if (totalSize > MAX_ZIP_SIZE_BYTES) {
      showToastMessage(tr('AttachementComponent.downloadZipTooLarge', 'Selected files are larger than 500 MB in total'), null, 'warn');
      return;
    }
    const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
    const fileName = downloadZipApplicationId ? `${downloadZipApplicationId}-attachments-${timestamp}.zip` : `attachments-${timestamp}.zip`;
    setDownloadZipBusy(true);
    downloadAttachmentsZip(
      { attachmentIds: selected.map(a => a.attachmentId) },
      (blob: Blob) => {
        setDownloadZipBusy(false);
        downloadBlob(blob, fileName);
      },
      securityCode,
      {
        onError: (_error: unknown, showStandardError: () => void) => {
          showStandardError();
          setDownloadZipBusy(false);
        },
        onValidationFailed: (errors: { message?: string }[]) => {
          setDownloadZipBusy(false);
          showToastMessage(
            tr('generic.error', 'Error'),
            (errors ?? []).map((error, index) => <div key={index}>{error.message}</div>),
            'error',
          );
        },
      },
    );
  };

  const [previewFileDataURL, setPreviewFileDataURL] = useState<string>();
  const viewDocument = (attachmentId: string) => {
    downloadAttachment(
      attachmentId,
      data => {
        // @ts-ignore
        const linkSource = `data:${data.mimeType};base64,${data.data}`;
        setPreviewFileDataURL(linkSource);
      },
      securityCode,
    );
  };

  const { selectedObjects, setSelectedObjects, toggleCheckbox, isSelected } = useMassAction<AttachmentUploadedOutDTO>(
    (a1, a2) => a1.attachmentId === a2.attachmentId,
    selectedObjects => {
      return (
        <>
          <Button
            label={tr(`AttachementComponent.massEdit`, `Hromadná editace`)}
            disabled={selectedObjects.length === 0 || disabled}
            onClick={e => setMassActionVisible(true)}
          />
          <Button
            label={tr(`AttachementComponent.shareWithEmail`, `Share with email`)}
            disabled={selectedObjects.length === 0 || disabled || doNotShare}
            onClick={e => setVisibleShareDialog(true)}
          />
          {enableCopyDocument && (
            <Button
              label={tr(`AttachementComponent.copyAttachments`, `Copy attachments`)}
              disabled={selectedObjects.length === 0 || disabled}
              onClick={e => setVisibleCopyDocuments(true)}
            />
          )}
        </>
      );
    },
    () => [],
  );

  const handleAttachmentReprocess = (attachment: AttachmentUploadedOutDTO) => {
    genericReProcess(
      {
        entityType: entityType,
        entityIds: [entityId],
        attachments: [
          {
            attachmentId: attachment.attachmentId,
            documentType: attachment.documentType,
            userNote: attachment.userNote,
            userDateLocalDate: attachment.userDateLocalDate,
            accessType: attachment.accessType,
            labelIds: [],
          },
        ],
      },
      () => {
        reloadAfterChange();
      },
    );
  };

  return {
    attachments,
    canEditInvoicesFlag,
    deleteDocumentForEntity,
    downloadFile,
    expandedRows,
    handleAttachmentReprocess,
    isSelected,
    load,
    reloadAfterChange,
    previewFileDataURL,
    securityCode,
    selectedAttachment,
    selectedObjects,
    setExpandedRows,
    setMassActionVisible,
    setPreviewFileDataURL,
    setSelectedAttachment,
    setSelectedObjects,
    setVisibleCopyDocuments,
    setVisibleEditInvoiceSidebar,
    setVisibleEditSidebar,
    setVisibleShareDialog,
    setVisibleTestSidebar,
    setVisibleUploadDialog,
    testRunsFlag,
    toggleCheckbox,
    viewDocument,
    visibleCopyDocuments,
    visibleEditInvoiceSidebar,
    visibleEditSidebar,
    visibleSendTestSidebar,
    visibleShareDialog,
    visibleUploadDialog,
    MassActionAttachment,
    doNotShare,
    enableCopyDocument,
    enableDownloadZip,
    downloadZip,
    downloadZipBusy,
  };
};

export type AttachmentInternals = ReturnType<typeof useAttachmentInternals>;

// Module scope on purpose: a component declared inside the hook is a new type on every render of the
// owning page, and React remounts it — dropping the list and refetching. See useSidebarControl.
const AttachmentComponent: React.FC<ComponentProps & { internals: AttachmentInternals }> = ({
  onAddAttachment,
  onDeletedAttachment,
  onDeleteMultipleAttachments,
  entityId,
  entityType,
  disabled,
  disabledUpload = disabled,
  disabledDelete = false,
  disabledMassAction = false,
  allowAddNewAttachment = true,
  allowEditAttachment = true,
  onAttachmentEdited,
  secured,
  hideHeader = false,
  internals,
}) => {
  const {
    attachments,
    canEditInvoicesFlag,
    deleteDocumentForEntity,
    downloadFile,
    expandedRows,
    handleAttachmentReprocess,
    isSelected,
    load,
    reloadAfterChange,
    previewFileDataURL,
    securityCode,
    selectedAttachment,
    selectedObjects,
    setExpandedRows,
    setMassActionVisible,
    setPreviewFileDataURL,
    setSelectedAttachment,
    setSelectedObjects,
    setVisibleCopyDocuments,
    setVisibleEditInvoiceSidebar,
    setVisibleEditSidebar,
    setVisibleShareDialog,
    setVisibleTestSidebar,
    setVisibleUploadDialog,
    testRunsFlag,
    toggleCheckbox,
    viewDocument,
    visibleCopyDocuments,
    visibleEditInvoiceSidebar,
    visibleEditSidebar,
    visibleSendTestSidebar,
    visibleShareDialog,
    visibleUploadDialog,
    MassActionAttachment,
    doNotShare,
    enableCopyDocument,
    enableDownloadZip,
    downloadZip,
    downloadZipBusy,
  } = internals;

  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { showToastMessage } = useContext(ToastContext);
  const { decorate } = useSecuredDecoration(secured);

  const timezoneToUse = loggedUser?.defaultTimezoneId || 'Europe/Prague';

  const onDeleteAttachmentHandler = (attachmentIdToDelete: string) => {
    if (onDeletedAttachment) {
      onDeletedAttachment(attachmentIdToDelete);
    } else {
      deleteDocumentForEntity(entityType, entityId, attachmentIdToDelete, () => reloadAfterChange());
    }
  };

  const handleDeleteSelected = () => {
    if (selectedObjects.length === 0) return;
    // RD-3076: deleting a file still being sent to Notino makes the upload fail; the BE refuses it too.
    if (selectedObjects.some(a => a.attachmentWebhookState === AttachmentWebhookStateEnum.PENDING)) {
      showToastMessage(
        tr('AttachementComponent.webhookStillProcessingTitle', 'Still processing'),
        tr('AttachementComponent.webhookStillProcessing', 'The eTransport RO is still being processed. Wait until the UIT or an error is shown, then try again.'),
        'warn',
      );
      return;
    }
    const idsToDelete = selectedObjects.map(obj => obj.attachmentId);
    if (onDeleteMultipleAttachments) {
      onDeleteMultipleAttachments(idsToDelete);
    } else if (onDeletedAttachment) {
      idsToDelete.forEach(id => onDeletedAttachment(id));
    } else {
      idsToDelete.forEach(id =>
        deleteDocumentForEntity(entityType, entityId, id, () => {
          if (idsToDelete.indexOf(id) === idsToDelete.length - 1) reloadAfterChange();
        }),
      );
    }
    setSelectedObjects([]);
  };
  const handleShareSelected = () => {
    setVisibleShareDialog(true);
  };
  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      setSelectedObjects(attachments);
    } else {
      setSelectedObjects([]);
    }
  };
  const allSelected = attachments?.length > 0 && selectedObjects?.length === attachments?.length;

  const aiInvoiceEnabled = useSecuredFlag('attachments-ai-invoice', {
    kind: 'visibility',
    description: 'AI invoice validation button',
    features: [FeaturesEnum.AI_INVOICE_VALIDATION],
  }).granted;

  const toggleExpandedRow = (row: AttachmentUploadedOutDTO) => {
    setExpandedRows(prev => {
      const exists = prev?.some(r => r.attachmentId === row.attachmentId);
      if (exists) {
        return prev?.filter(r => r.attachmentId !== row.attachmentId);
      } else {
        return [...prev, row];
      }
    });
  };

  const notProcessedText = <span className="text-complementary-error">{tr('ai.invoice.detail.notProcessed', 'Not processed')}</span>;

  const getValueOrUndefined = (value?: StringWithErrorsDTO) => {
    return value?.value ?? notProcessedText;
  };

  const getStringOrUndefined = (value?: string) => {
    return value ?? notProcessedText;
  };

  const getNumberOrUndefined = (value?: BigDecimalWithErrorsDTO, multiplier: number = 1, fractionDigits: number = 2) => {
    return value?.value != null ? formatNumber(value.value * multiplier, fractionDigits) : notProcessedText;
  };

  const getTaxRateOrUndefined = (taxRate?: BigDecimalWithErrorsDTO) => {
    if (taxRate?.value != null) {
      return <>{formatNumber(taxRate.value * 100, 2)}%</>;
    }
    const hasMultipleRates = taxRate?.errors?.some(
      e => e.invoiceSeverity === InvoiceSeverityEnum.INFO && e.messageKey === 'invoiceMapping.taxRate.multiple.rates.detected',
    );
    if (hasMultipleRates) {
      return <span>{tr('invoiceMapping.taxRate.multiple.rates.detected', 'Multiple tax rates')}</span>;
    }
    return notProcessedText;
  };

  const getDateOrUndefined = (value?: StringWithErrorsDTO) => {
    return value?.value ? formatDateFromStringWithLocale(value.value) : notProcessedText;
  };

  const getPriceOrUndefined = (value?: InvoicePriceDTO, includeOriginal: boolean = true) => {
    if (!value) return notProcessedText;

    const isError = value.errors && value.errors.some(error => error.invoiceSeverity == InvoiceSeverityEnum.ERROR);
    const isWarning = value.errors && value.errors.some(error => error.invoiceSeverity == InvoiceSeverityEnum.WARNING);

    const priceString =
      value?.amount != null ? (
        <span className={isError ? 'text-complementary-error' : isWarning ? 'text-complementary-warning' : undefined}>{formatPrice(value.amount, value.currency)}</span>
      ) : (
        notProcessedText
      );
    const companyPriceString = value?.originalAmount != null ? `${formatPrice(value.originalAmount, value.originalCurrency)}` : notProcessedText;
    return (
      <span>
        {priceString} {includeOriginal && `${tr('ai.invoice.detail.originalValue', 'original values')} ${companyPriceString}`}
      </span>
    );
  };

  const getAmountOrUndefined = (amount?: number, currency?: string) => {
    if (amount == null) return notProcessedText;
    return `${formatPrice(amount, currency)}`;
  };

  const getErrorIcon = (errors?: InvoiceErrorDTO[]) => {
    if (!errors) return null;

    const isError = errors.some(error => error.invoiceSeverity === InvoiceSeverityEnum.ERROR);
    const isWarning = errors.some(error => error.invoiceSeverity === InvoiceSeverityEnum.WARNING);

    if (!isError && !isWarning) return null;

    const errorElements = [];

    if (isError) {
      const errorId = `error_${Math.random().toString(36).substr(2, 9)}`;
      const errorMessages = errors
        .filter(e => e.invoiceSeverity === InvoiceSeverityEnum.ERROR)
        .map(e => tr(`error.${e.messageKey}`, e.message, getInvoiceMessageArgs(e.messageArgs)))
        .join(', ');

      errorElements.push(
        <div key="error">
          <SvgERROR id={errorId} className="w-4 h-4 text-complementary-error" />
          <CustomTooltip targetSelector={`#${errorId}`} content={errorMessages} />
        </div>,
      );
    }

    if (isWarning) {
      const warningId = `warning_${Math.random().toString(36).substr(2, 9)}`;
      const warningMessages = errors
        .filter(e => e.invoiceSeverity === InvoiceSeverityEnum.WARNING)
        .map(e => tr(`error.${e.messageKey}`, e.message, getInvoiceMessageArgs(e.messageArgs)))
        .join(', ');

      errorElements.push(
        <div key="warning">
          <SvgWARNING id={warningId} className="w-4 h-4 text-complementary-warning" />
          <CustomTooltip targetSelector={`#${warningId}`} content={warningMessages} />
        </div>,
      );
    }

    return <span className="flex flex-row gap-1">{errorElements}</span>;
  };

  const rowExpansionTemplate = (row: AttachmentUploadedOutDTO) => {
    const actionInfo = getInvoiceActionInfo(row.invoiceData.status);
    return (
      <div
        className={clsx('flex flex-col p-4 gap-4 rounded-lg border-[3px] border-solid bg-base-tertiary mx-auto', getBorderClassForInvoiceState(row.invoiceData.status))}
      >
        <div className={`flex flex-row gap-4 justify-between items-center w-full`}>
          <div className={'flex flex-row gap-2 justify-start items-center'}>
            <span className="relative flex items-center gap-2">
              <SvgRobot className="w-6 h-6" />
              {getErrorIcon(row.invoiceData.stateErrors)}
              {tr('ai.invoice.status.' + row.invoiceData.status, row.invoiceData.status)}
              <RingilButton
                label={tr('ai.invoice.detail.rerun', 'Rerun analysis')}
                icon={<SvgRepeat />}
                size={'small'}
                onClick={() => {
                  handleAttachmentReprocess(row);
                }}
              />
            </span>
          </div>
          <SvgClose
            className="w-4 h-4 text-content-brand"
            onClick={() => {
              toggleExpandedRow(row);
            }}
          />
        </div>
        {actionInfo && (
          <COInlineMessage variant={'info'} icon={<SvgGrayInfo />} dataTestId={'ai-invoice-action-info'}>
            <p>
              {tr(actionInfo.textKey, actionInfo.textDefault)}{' '}
              <strong>{tr(actionInfo.emphasisKey, actionInfo.emphasisDefault)}</strong>
            </p>
          </COInlineMessage>
        )}
        <div>
          <table>
            <tbody>
              <tr>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.carrierName', 'Carrier name')}
                    {getErrorIcon(row.invoiceData.carrierName?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getValueOrUndefined(row.invoiceData.carrierName)}</td>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.totalPrice', 'Total price')}
                    {getErrorIcon([...(row.invoiceData.taxExclusiveAmounts[0]?.errors ?? []), ...(row.invoiceData.taxExclusiveAmounts[1]?.errors ?? [])])}
                  </span>
                </td>
                <td className="w-[30%]">{getPriceOrUndefined(row.invoiceData.taxExclusiveAmounts[0], false)}</td>
              </tr>
              <tr>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.tin', 'TIN')}
                    {getErrorIcon(row.invoiceData.tin?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getValueOrUndefined(row.invoiceData.tin)}</td>
                <td className="w-[20%] text-[14px] font-bold"></td>
                <td className="w-[30%]">{row.invoiceData.taxExclusiveAmounts[1] && getPriceOrUndefined(row.invoiceData.taxExclusiveAmounts[1], false)}</td>
              </tr>
              <tr>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.invoiceNumber', 'Invoice number')}
                    {getErrorIcon(row.invoiceData.invoiceNumber?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getValueOrUndefined(row.invoiceData.invoiceNumber)}</td>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.vat', 'VAT')}
                    {getErrorIcon([...(row.invoiceData.taxAmounts[0]?.errors ?? []), ...(row.invoiceData.taxAmounts[1]?.errors ?? [])])}
                  </span>
                </td>
                <td className="w-[30%]">{getPriceOrUndefined(row.invoiceData.taxAmounts[0], false)}</td>
              </tr>
              <tr>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.taxPointDate', 'Tax point date')}
                    {getErrorIcon(row.invoiceData.taxPointDate?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getDateOrUndefined(row.invoiceData.taxPointDate)}</td>
                <td className="w-[20%] text-[14px] font-bold"></td>
                <td className="w-[30%]">{row.invoiceData.taxAmounts[1] && getPriceOrUndefined(row.invoiceData.taxAmounts[1], false)}</td>
              </tr>
              <tr>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.issueDate', 'Issue date')}
                    {getErrorIcon(row.invoiceData.issueDate?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getDateOrUndefined(row.invoiceData.issueDate)}</td>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.vatValue', 'VAT value')}
                    {getErrorIcon(row.invoiceData.taxRate?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getTaxRateOrUndefined(row.invoiceData.taxRate)}</td>
              </tr>
              <tr>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.variableSymbol', 'Variable symbol')}
                    {getErrorIcon(row.invoiceData.variableSymbol?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getValueOrUndefined(row.invoiceData.variableSymbol)}</td>
                <td className="w-[20%] text-[14px] font-bold">
                  <span className="flex flex-row gap-2">
                    {tr('ai.invoice.detail.exchangeRate', 'Exchange rate')}
                    {getErrorIcon(row.invoiceData.exchangeRate?.errors)}
                  </span>
                </td>
                <td className="w-[30%]">{getNumberOrUndefined(row.invoiceData.exchangeRate)}</td>
              </tr>
            </tbody>
          </table>
          {row.invoiceData.items && row.invoiceData.items.length > 0 && (
            <>
              <div className="mt-4 mb-4 border-b border-t-0 border-solid border-content-tertiary" />
              <div className="flex flex-col gap-2">
                <COHeadline variant={'h4'} title={tr('ai.invoice.detail.items', 'Items')} />
                <table>
                  <tbody>
                    {row.invoiceData.items.map((item, index) => (
                      <tr key={index}>
                        <td className="w-[20%] text-[14px] font-bold">
                          <span className="flex flex-row gap-2">
                            {index + 1}.&nbsp;
                            {tr('ai.invoice.detail.item.title', 'Item number:')}
                            {getErrorIcon(item.errors ?? [])}
                          </span>
                        </td>
                        <td
                          className={clsx(
                            'w-[80%]',
                            item.errors?.some(e => e.invoiceSeverity === InvoiceSeverityEnum.ERROR) && 'text-complementary-error',
                            item.errors?.some(e => e.invoiceSeverity === InvoiceSeverityEnum.WARNING) &&
                              !item.errors?.some(e => e.invoiceSeverity === InvoiceSeverityEnum.ERROR) &&
                              'text-complementary-warning',
                          )}
                        >
                          {tr('ai.invoice.detail.item.description', '{{itemId}} - {{withTaxAmount}} ({{withoutTaxAmount}}, VAT {{taxAmount}})', {
                            itemId: getStringOrUndefined(item.id),
                            withTaxAmount: getAmountOrUndefined(item.withTaxAmount, item.currency),
                            withoutTaxAmount: getAmountOrUndefined(item.withoutTaxAmount, item.currency),
                            taxAmount: getAmountOrUndefined(item.taxAmount, item.currency),
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="flex px-4 text-content-secondary font-dm-sans text-[8px] font-normal not-italic leading-[12px]">
          {tr('ai.disclaimer', 'The AI classification is provided for informational purposes only and may not always be accurate.')}
        </div>
      </div>
    );
  };

  return decorate(
    <AttachmentTableWrapper>
      {!hideHeader && (
        <StyledRingilH2 className="flex items-center gap-2">
          <LinkSvg width={26} height={26} fill="#B2B2B2" /> {tr(`AttachmentComponent.documents`, `Documents`)}
        </StyledRingilH2>
      )}

      <ActionPanel
        allSelected={allSelected}
        selectedCount={selectedObjects.length}
        handleSelectAllChange={handleSelectAllChange}
        handleMassEdit={() => setMassActionVisible(true)}
        handleShareSelected={handleShareSelected}
        handleCopySelected={() => setVisibleCopyDocuments(true)}
        enableCopyDocument={enableCopyDocument}
        handleDownloadZip={() => downloadZip(selectedObjects)}
        enableDownloadZip={enableDownloadZip}
        downloadZipBusy={downloadZipBusy}
        handleDeleteSelected={handleDeleteSelected}
        disabled={disabled}
        disabledDelete={disabledDelete}
        disabledMassAction={disabledMassAction}
        doNotShare={doNotShare}
        tr={tr}
      />

      <DataTable
        value={attachments}
        emptyMessage={tr(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}
        className="ringil-table"
        expandedRows={expandedRows}
        rowExpansionTemplate={rowExpansionTemplate}
      >
        <Column
          header={''}
          body={(row: AttachmentUploadedOutDTO) => (
            <Checkbox
              onChange={e => {
                toggleCheckbox(row);
              }}
              checked={isSelected(row)}
              disabled={disabled && !enableDownloadZip}
            />
          )}
          style={{ width: 30 }}
        ></Column>
        <Column
          header={tr(`AttachmentComponent.documentName`, `Document name`)}
          body={(row: AttachmentUploadedOutDTO) => {
            const resolvedLabelsForRow = (row as any).labels as LabelDefinition[];
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div>
                  <Clickable
                    onClick={e => {
                      downloadFile(row.attachmentId);
                    }}
                  >
                    <div className="font-medium" style={{ lineBreak: 'auto', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {row.fileName}
                    </div>
                  </Clickable>
                  <div className="badge-row">
                    <div className={'p-mt-4'}>
                      <AttachmentLabelsBar attachmentId={row.attachmentId} resolvedLabels={resolvedLabelsForRow || []} securityCode={securityCode} />
                    </div>
                  </div>
                </div>
                {row.fileName.endsWith('.pdf') && (
                  <IconButton
                    className="inline-flex gap-2 border border-solid bg-white px-[6px] py-[9px] items-center leading-3 h-[32px] cursor-pointer self-start rounded"
                    onClick={e => viewDocument(row.attachmentId)}
                    title={tr('AttachementComponent.viewPdf', 'View PDF')}
                  >
                    <EyeSvg width={17} height={16} fill="#616061" />
                  </IconButton>
                )}
              </div>
            );
          }}
        ></Column>
        <Column
          header={tr(`AttachmentComponent.uploadedBy`, `Uploaded by`)}
          body={(row: AttachmentUploadedOutDTO) => (
            <div>
              <div>{row.userName}</div>
              <div>{row.companyName}</div>
              <Note>{stringDateTimeWithLocaleFromApiStringInTz(row.uploadedAt, timezoneToUse)}</Note>
              <COTimezone timezoneId={timezoneToUse} />
            </div>
          )}
        ></Column>
        <Column
          header={tr(`AttachmentComponent.documentType`, `Document type`)}
          body={(row: AttachmentUploadedOutDTO) => (
            <div className="flex flex-col gap-2">
              <DocumentTypeTag>{row.documentTypeName}</DocumentTypeTag>
              {(row.isAiProcessingEnabledForOwnerCompany || aiInvoiceEnabled) && row.invoiceData && row.documentType === DocumentTypeEnum.INVOICE_CARRIER && (
                <div onClick={() => toggleExpandedRow(row)}>
                  <AiFileButton state={row.invoiceData.status} />
                </div>
              )}
              {row.accessType === AccessTypeEnum.INTERNAL && <InternalTag>{tr(`AttachementComponent.internal`, `Internal`)}</InternalTag>}
              {row.accessType === AccessTypeEnum.ALL && <PublicTag>{tr(`AttachementComponent.public`, `Public`)}</PublicTag>}
            </div>
          )}
        ></Column>
        <Column
          header={tr(`AttachmentComponent.userDate1`, `Date`)}
          body={(row: AttachmentUploadedOutDTO) => (
            <div>
              <div>{row.userDateLocalDate ? formatDateFromStringWithLocale(row.userDateLocalDate) : ''}</div>
            </div>
          )}
        ></Column>
        <Column
          header={tr(`AttachmentComponent.note`, `Note`)}
          body={(row: AttachmentUploadedOutDTO) => (
            <div className={'flex flex-col gap-1 items-start'}>
              <WebhookStatusBadge row={row} tr={tr} />
              <Note>{row.userNote}</Note>
            </div>
          )}
        ></Column>
        <Column
          body={(row: AttachmentUploadedOutDTO) =>
            allowEditAttachment && !disabled ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Details style={{ display: 'inline-block' }}>
                  <Summary title={tr('AttachmentComponent.more', 'More actions')}>
                    <SvgMoreButton fill="#616061" />
                  </Summary>
                  <Menu>
                    <div
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        color: '#616061',
                        fontFamily: 'DM Sans, sans-serif',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => {
                        setSelectedAttachment(row);
                        setVisibleEditSidebar(true);
                      }}
                    >
                      {tr('AttachmentComponent.editProperties', 'Edit properties')}
                    </div>
                    <SecuredArea
                      id="attachments-edit-invoice" instance={row.attachmentId}
                      label="Edit Invoice" description="Edit the data of a carrier invoice"
                      visible={[
                        canEditInvoicesFlag,
                        { type: 'data', label: 'documentType === INVOICE_CARRIER', satisfied: row.documentType === DocumentTypeEnum.INVOICE_CARRIER },
                      ]}
                    >
                      <>
                        <HorizontalLine variant={'brand-light'} />
                        <div
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            color: '#616061',
                            fontFamily: 'DM Sans, sans-serif',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => {
                            setSelectedAttachment(row);
                            setVisibleEditInvoiceSidebar(true);
                          }}
                        >
                          {tr('AttachmentComponent.editInvoice', 'Edit Invoice')}
                        </div>
                      </>
                    </SecuredArea>
                    <SecuredArea
                      id="attachments-run-test" instance={row.attachmentId}
                      label="Send test invoice data" description="Send test data of a carrier invoice"
                      visible={[
                        testRunsFlag,
                        { type: 'data', label: 'documentType === INVOICE_CARRIER', satisfied: row.documentType === DocumentTypeEnum.INVOICE_CARRIER },
                      ]}
                    >
                      <>
                        <HorizontalLine variant={'brand-light'} />
                        <div
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            color: '#616061',
                            fontFamily: 'DM Sans, sans-serif',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => {
                            setSelectedAttachment(row);
                            setVisibleTestSidebar(true);
                          }}
                        >
                          {tr('AttachmentComponent.sendTestDataForInvoice', 'Send test invoice data')}
                        </div>
                      </>
                    </SecuredArea>
                  </Menu>
                </Details>
              </div>
            ) : null
          }
        ></Column>
      </DataTable>

      {attachments?.length >= 5 && (
        <ActionPanel
          allSelected={allSelected}
          selectedCount={selectedObjects.length}
          handleSelectAllChange={handleSelectAllChange}
          handleMassEdit={() => setMassActionVisible(true)}
          handleShareSelected={handleShareSelected}
          handleCopySelected={() => setVisibleCopyDocuments(true)}
          enableCopyDocument={enableCopyDocument}
          handleDownloadZip={() => downloadZip(selectedObjects)}
          enableDownloadZip={enableDownloadZip}
          downloadZipBusy={downloadZipBusy}
          handleDeleteSelected={handleDeleteSelected}
          disabled={disabled}
          disabledDelete={disabledDelete}
          disabledMassAction={disabledMassAction}
          doNotShare={doNotShare}
          tr={tr}
        />
      )}

      {allowAddNewAttachment && !disabledUpload && (
        <div className={'mt-4'}>
          <RowButton
            title={tr('AttachementComponent.addAttachment', 'Add attachment')}
            icon={<SvgLink width={32} height={32} className={'text-content-quaternary'} />}
            onClick={() => setVisibleUploadDialog(true)}
          />
        </div>
      )}

      {visibleUploadDialog &&
        (onAddAttachment ? (
          <UploadAttachmentDialog
            visible={visibleUploadDialog}
            onHide={() => setVisibleUploadDialog(false)}
            onCompleted={v => {
              onAddAttachment(
                v.map(t => {
                  return {
                    ...t,
                    documentType: {
                      documentType: t.documentType,
                      name: t.documentTypeName,
                    },
                  } as AttachmentDocument;
                }),
              );
              setVisibleUploadDialog(false);
            }}
          />
        ) : (
          <UploadAttachmentDialog
            visible={visibleUploadDialog}
            onHide={() => setVisibleUploadDialog(false)}
            entityType={entityType}
            entityIds={[entityId]}
            onAttached={() => {
              setVisibleUploadDialog(false);
              reloadAfterChange();
            }}
          />
        ))}

      <ShareDocumentDialog
        visible={visibleShareDialog}
        onHide={() => setVisibleShareDialog(false)}
        documents={selectedObjects}
        entityId={entityId}
        entityType={entityType}
      />

      <CopyDocumentsDialog
        visible={visibleCopyDocuments}
        onHide={() => setVisibleCopyDocuments(false)}
        documents={selectedObjects}
        entityId={entityId}
        entityType={entityType}
      />

      <MassActionAttachment
        selectedAttachments={selectedObjects.map(a => ({
          attachmentId: a.attachmentId,
          fileName: a.fileName,
          labels: (a as any).labels as LabelDefinition[] | undefined,
        }))}
        onComplete={() => {
          setSelectedObjects([]);
          reloadAfterChange();
        }}
      />

      {selectedAttachment && visibleEditSidebar && (
        <EditAttachmentSidebar
          visible={visibleEditSidebar}
          onHide={() => {
            setVisibleEditSidebar(false);
            setSelectedAttachment(null);
          }}
          attachment={selectedAttachment}
          onUpdated={updateFromSidebar => {
            setVisibleEditSidebar(false);
            setSelectedAttachment(null);
            if (entityId === null && onAttachmentEdited) {
              onAttachmentEdited(updateFromSidebar);
            } else if (entityId !== null) {
              reloadAfterChange();
            }
          }}
        />
      )}

      {visibleSendTestSidebar && selectedAttachment && (
        <SendTestInvoiceDataSidebar
          attachmentId={selectedAttachment.attachmentId}
          parentEntityId={entityId}
          s3Path={''}
          visible={visibleSendTestSidebar}
          onHide={() => {
            console.log('called on hide');
            setVisibleTestSidebar(false);
            setSelectedAttachment(null);
          }}
        />
      )}

      {visibleEditInvoiceSidebar && selectedAttachment && (
        <EditInvoiceSidebar
          visible={visibleEditInvoiceSidebar}
          onHide={() => {
            setVisibleEditInvoiceSidebar(false);
            setSelectedAttachment(null);
          }}
          attachmentId={selectedAttachment.attachmentId}
          onUpdated={() => {
            reloadAfterChange();
          }}
        />
      )}

      <PDFViewerDialog pdfData={previewFileDataURL} onHide={() => setPreviewFileDataURL(null)} />
    </AttachmentTableWrapper>,
  );
};

export const useCreateAttachmentComponent = (options: AttachmentComponentOptions, componentProps: ComponentProps) => {
  const internals = useAttachmentInternals(options, componentProps);
  const { load, attachments } = internals;
  return { AttachmentComponent, internals, load, attachments };
};

/** State of the upload webhook (Notino eTransport RO) of one file, RD-3076. */
const WebhookStatusBadge: React.FC<{ row: AttachmentUploadedOutDTO; tr: (key: string, fallback: string) => string }> = ({ row, tr }) => {
  switch (row.attachmentWebhookState) {
    case AttachmentWebhookStateEnum.PENDING:
      return (
        <COBadge type={'processing'} variant={'outlined'} icon={<i className={'pi pi-spin pi-spinner'} />}>
          {tr('AttachementComponent.webhookPending', 'Sending eTransport…')}
        </COBadge>
      );
    case AttachmentWebhookStateEnum.SENT:
      return (
        <COBadge type={'positive'} variant={'outlined'}>
          {tr('AttachementComponent.webhookSent', 'UIT received')}
        </COBadge>
      );
    case AttachmentWebhookStateEnum.FAILED:
      return (
        <COBadge type={'warning'} variant={'outlined'}>
          {tr('AttachementComponent.webhookFailed', 'eTransport sending failed')}
        </COBadge>
      );
    default:
      return null;
  }
};
